<template>
  <div class="personal-info-container" v-bind="attrs">
    <!-- 标题 -->
    <div class="title">基础数据</div>

    <!-- 主要内容区域 -->
    <div class="main-content">
      <div class="data-card">
        <div class="form-content">
        <!-- 身体类型选择列表 -->
        <!-- 身高选择 -->
        <div class="form-group">
          <label class="form-label">身高(cm)</label>
          <div class="horizontal-picker-container">
            <div class="horizontal-picker-overlay">
              <div class="horizontal-picker-center"></div>
            </div>
            <div class="horizontal-picker-scroll" ref="heightPicker" @scroll="handleHeightScroll">
              <div class="horizontal-picker-padding"></div>
              <div
                v-for="(h, index) in heightRanges"
                :key="index"
                :class="['horizontal-picker-item', { 'selected': selectedHeightIndex === index }]"
                @click="selectHeight(index)"
              >
                {{ h }}
              </div>
              <div class="horizontal-picker-padding-right"></div>
            </div>
          </div>
        </div>
        
        <!-- 体重选择 -->
        <div class="form-group">
          <label class="form-label">体重(kg)</label>
          <div class="horizontal-picker-container">
            <div class="horizontal-picker-overlay">
              <div class="horizontal-picker-center"></div>
            </div>
            <div class="horizontal-picker-scroll" ref="weightPicker" @scroll="handleWeightScroll">
              <div class="horizontal-picker-padding"></div>
              <div
                v-for="(w, index) in weightRanges"
                :key="index"
                :class="['horizontal-picker-item', { 'selected': selectedWeightIndex === index }]"
                @click="selectWeight(index)"
              >
                {{ w }}
              </div>
              <div class="horizontal-picker-padding-right"></div>
            </div>
          </div>
        </div>
        
        </div>
      </div>
    </div>
    
    <!-- 底部操作按钮 -->
    <div class="bottom-actions">
      <button class="btn-secondary" @click="goBack">
        <span class="btn-icon"><img :src="backIcon"></span>
        <span class="btn-text">上一步</span>
      </button>
      <button 
        class="btn-primary" 
        :class="{ disabled: !isFormValid || isLoading, loading: isLoading }"
        :disabled="!isFormValid || isLoading"
        @click="handleSubmit"
      >
        <!-- 加载状态 -->
        <div v-if="isLoading" class="loading-content">
          <div class="loading-spinner"></div>
          <div class="loading-text">处理中...</div>
        </div>
        <!-- 正常状态 -->
        <div v-else class="button-content">
          <span class="btn-icon"><img :src="confirmIcon"></span>
          <span class="btn-text">确定</span>
        </div>
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick, onMounted, onBeforeUnmount, useAttrs } from 'vue'
const confirmIcon = require('@/assets/images/svg/confirm.svg')
const backIcon = require('@/assets/images/svg/back.svg')
import hourglassImg from "@/assets/images/character/hourglass.svg";
import hourglassImgCheck from "@/assets/images/character/hourglassImgCheck.svg";
import triangleImg from "@/assets/images/character/triangle.svg";
import triangleImgCheck from "@/assets/images/character/triangleCheck.svg";
import invertedTriangle from "@/assets/images/character/invertedTriangle.svg";
import invertedTriangleCheck from "@/assets/images/character/invertedTriangleCheck.svg";
import shapedBody from "@/assets/images/character/shapedbody.svg";
import shapedBodyCheck from "@/assets/images/character/shapedbodyCheck.svg";
import rectangle from "@/assets/images/character/rectangle.svg";
import rectangleCheck from "@/assets/images/character/rectangleCheck.svg";
import okImg from "@/assets/images/svg/ok.svg";
import { StartExploreApi } from '@/api/startExplore'
import {expiredStorage} from "@/utils/expiredStorage";
const bodies = [
    {name: 'hourglass', image: hourglassImg, imageCheck: hourglassImgCheck},
    {name: 'Triangle', image: triangleImg, imageCheck: triangleImgCheck},
    {name: 'Inverted triangle', image: invertedTriangle, imageCheck: invertedTriangleCheck},
    {name: 'O-shaped body', image: shapedBody, imageCheck: shapedBodyCheck},
    {name: 'rectangle', image: rectangle, imageCheck: rectangleCheck}
]
// 定义 emits
const emit = defineEmits(['next', 'back', 'validation-change', 'data-change'])

// 定义 props 来接收回填数据
const props = defineProps({
  initialData: {
    type: Object,
    default: () => ({})
  },
  // 接收来自FormContainer的所有表单数据
  allFormData: {
    type: Object,
    default: () => ({})
  }
})

// 获取 attrs（不包含已声明的 props），用于绑定到根元素以避免警告
// 由于 initialData 和 allFormData 已在 props 中声明，它们不会出现在 attrs 中
const attrs = useAttrs()

const bodyType = ref(props.initialData.bodyType || 'O-shaped body')
const hoveredBody = ref(null)

// 身高范围数据
const heightRanges = ref([
    '< 150','150-155', '155-160', '160-165', '165-170', '170-175', '175-180', '180-185', '185-190','> 190','',''
])

// 体重范围数据
const weightRanges = ref([
    '< 40','40-45', '45-50', '50-55', '55-60', '60-65', '65-70', '70-75', '75-80', '80-85', '85-90','> 90','',''
])

// 响应式数据
const selectedHeight = ref(props.initialData.height || '165-170')
const selectedWeight = ref(props.initialData.weight || '55-60')
const isLoading = ref(false)

// 选择器索引
const selectedHeightIndex = ref(heightRanges.value.findIndex(h => h === selectedHeight.value))
const selectedWeightIndex = ref(weightRanges.value.findIndex(w => w === selectedWeight.value))

// 如果找不到，默认选中中间
if (selectedHeightIndex.value === -1) {
  selectedHeightIndex.value = Math.floor(heightRanges.value.length / 2)
  selectedHeight.value = heightRanges.value[selectedHeightIndex.value]
}
if (selectedWeightIndex.value === -1) {
  selectedWeightIndex.value = Math.floor(weightRanges.value.length / 2)
  selectedWeight.value = weightRanges.value[selectedWeightIndex.value]
}

// Refs for picker elements
const heightPicker = ref(null)
const weightPicker = ref(null)

// 添加缺失的变量定义
const email = ref('') // 邮箱变量
const height = ref(selectedHeight.value) // 身高变量
const weight = ref(selectedWeight.value) // 体重变量
const uploadedImage = ref(null) // 上传的图片变量
const bodyImageFor = (b) => {
    // If the body is the currently selected one, use the checked image if available
    const selected = bodyType.value === b.name
    if (selected && b.imageCheck) return b.imageCheck
    return b.image
}
// 计算范围的中间值
const calculateMiddleValue = (range) => {
    if (!range || typeof range !== 'string') return ''
    
    // 处理特殊情况：< 40, < 150, > 90, > 190 等，直接返回整数
    if (range && (range.includes('<') || range.includes('>'))) {
        // 提取数字部分
        const num = parseFloat(range.replace(/[<>]/g, '').trim())
        if (!isNaN(num)) {
            return Math.round(num).toString()
        }
        return range
    }
    
    // 检查是否是范围格式 (例如: "165-170")
    if (range && range.includes('-')) {
        const parts = range.split('-')
        if (parts.length === 2) {
            const min = parseFloat(parts[0])
            const max = parseFloat(parts[1])
            if (!isNaN(min) && !isNaN(max)) {
                return ((min + max) / 2).toString()
            }
        }
    }
    
    // 如果不是范围格式，直接返回原值
    return range
}

const ok= async ()=>{
    // 从allFormData中获取Step3的照片数据
    const step3Data = props.allFormData.step3 || {}
    const uploadResult = props.allFormData.uploadResult || null
    const payload = {
        userId: 'onboard-' + Date.now()
    }
    console.log('=== PersonalInfoStep2Content ok函数 ===')
    
    // 计算身高和体重的中间值
    const heightMiddle = calculateMiddleValue(height.value)
    const weightMiddle = calculateMiddleValue(weight.value)
    
    const req={
        face_image:step3Data.avatarFile,
        // body_description:`${height.value}cm, ${weight.value}kg`,
        height: heightMiddle,
        weight: weightMiddle,
        shape:getBodyTypeName(bodyType.value)
    }
    console.log('最终payload:', req)
    console.log('身高范围:', height.value, '-> 折中值:', heightMiddle)
    console.log('体重范围:', weight.value, '-> 折中值:', weightMiddle)
    isLoading.value = true
    expiredStorage.setItem('user',payload)
    try {
        const token =  await StartExploreApi.startExplore(req)
        console.log('StartExplore API调用成功:', token)
        console.log('token完整对象:', JSON.stringify(token, null, 2))
        console.log('token.image_url:', token.image_url)
        
        // 检查 image_url 是否存在
        if (!token || !token.image_url) {
            console.error('token.image_url 不存在或为空:', token)
            throw new Error('startExplore 返回的 image_url 为空')
        }
        
        // 确保 payload 对象被正确更新
        payload.face_image_url = token.image_url
        console.log('更新后的payload:', JSON.stringify(payload, null, 2))
        
        // 重新保存到存储
        const saveResult = expiredStorage.setItem('user', payload)
        console.log('保存结果:', saveResult)
        
        if (!saveResult) {
            console.error('保存到 expiredStorage 失败')
            throw new Error('保存用户数据失败')
        }
        
        // 验证保存是否成功 - 使用 nextTick 确保存储操作完成
        await nextTick()
        const savedData = expiredStorage.getItem('user')
        console.log('保存后读取的数据:', JSON.stringify(savedData, null, 2))
        console.log('保存后的face_image_url:', savedData?.face_image_url)
        
        if (!savedData || !savedData.face_image_url) {
            console.error('验证失败：保存的数据中没有 face_image_url')
            // 通知数据保存失败
            StartExploreApi.notifyDataSaveFailed('验证失败：face_image_url 不存在')
            throw new Error('保存后验证失败：face_image_url 不存在')
        }
        
        console.log('✅ 数据保存成功，face_image_url:', savedData.face_image_url)
        
        // 通知数据保存已完成
        StartExploreApi.notifyDataSaved()
    } catch (error) {
        console.error('StartExplore API调用失败:', error)
        // 通知数据保存失败
        StartExploreApi.notifyDataSaveFailed(error)
        throw error // 重新抛出错误，让调用者知道失败
    } finally {
        isLoading.value = false
    }
}
// 获取身体类型图片，支持悬停效果
const getBodyImage = (b) => {
    // 如果当前身体类型被选中，使用选中状态的图片
    const selected = bodyType.value === b.name
    if (selected && b.imageCheck) return b.imageCheck
    
    // 如果当前身体类型被悬停，使用悬停状态的图片
    const hovered = hoveredBody.value === b.name
    if (hovered && b.imageCheck) return b.imageCheck
    
    // 否则使用默认图片
    return b.image
}

// 身体类型名称映射
const getBodyTypeName = (bodyTypeName) => {
  const nameMap = {
    'hourglass': '沙漏型',
    'Triangle': '梨形',
    'Inverted triangle': 'T字型',
    'O-shaped body': 'O字型',
    'rectangle': 'H型'
  }
  return nameMap[bodyTypeName] || bodyTypeName
}

// 计算属性
const isFormValid = computed(() => {
  return bodyType.value !== null && bodyType.value !== '' && 
         selectedHeight.value !== null && selectedHeight.value !== '' &&
         selectedWeight.value !== null && selectedWeight.value !== ''
})

// 监听验证状态变化并通知父组件
watch(isFormValid, (newValue) => {
  nextTick(() => {
    emit('validation-change', newValue)
  })
})

// 恢复数据的辅助函数
const restoreData = (data) => {
  if (!data || typeof data !== 'object') return
  
  let hasChanged = false
  
  // 恢复身体类型
  if (data.bodyType !== undefined && data.bodyType !== null && data.bodyType !== bodyType.value) {
    bodyType.value = data.bodyType
    hasChanged = true
  }
  
  // 恢复身高
  if (data.height !== undefined && data.height !== null && data.height !== selectedHeight.value) {
    selectedHeight.value = data.height
    const heightIdx = heightRanges.value.findIndex(h => h === selectedHeight.value)
    if (heightIdx !== -1) {
      selectedHeightIndex.value = heightIdx
      hasChanged = true
      nextTick(() => {
        if (heightPicker.value) {
          scrollToCenter(heightPicker.value, selectedHeightIndex.value)
        }
      })
    }
  }
  
  // 恢复体重
  if (data.weight !== undefined && data.weight !== null && data.weight !== selectedWeight.value) {
    selectedWeight.value = data.weight
    const weightIdx = weightRanges.value.findIndex(w => w === selectedWeight.value)
    if (weightIdx !== -1) {
      selectedWeightIndex.value = weightIdx
      hasChanged = true
      nextTick(() => {
        if (weightPicker.value) {
          scrollToCenter(weightPicker.value, selectedWeightIndex.value)
        }
      })
    }
  }
  
  if (hasChanged) {
    console.log('Step2: 从initialData恢复数据:', {
      bodyType: bodyType.value,
      height: selectedHeight.value,
      weight: selectedWeight.value
    })
  }
}

// 监听props变化，更新本地数据
watch(() => props.initialData, (newInitialData) => {
  restoreData(newInitialData)
}, { deep: true, immediate: true })

// 组件挂载后初始化选择器位置
onMounted(() => {
  // 再次恢复数据（确保数据正确）
  restoreData(props.initialData)

  nextTick(() => {
    // 初始化身高选择器位置
    if (heightPicker.value) {
      scrollToCenter(heightPicker.value, selectedHeightIndex.value)
    }
    // 初始化体重选择器位置
    if (weightPicker.value) {
      scrollToCenter(weightPicker.value, selectedWeightIndex.value)
    }
    
    emit('validation-change', isFormValid.value)
  })
})

// 验证邮箱格式
const validateEmailFormat = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

// 验证邮箱输入
const validateEmail = () => {
  // 可以在这里添加实时验证逻辑
}

// 验证姓名输入
const validateName = () => {
  // 可以在这里添加实时验证逻辑
}

// 验证身高输入
const validateHeight = () => {
  if (parseFloat(height.value) < 0) {
    height.value = ''
  }
}

// 验证体重输入
const validateWeight = () => {
  if (parseFloat(weight.value) < 0) {
    weight.value = ''
  }
}

// 身高选择
const selectHeight = (index) => {
  selectedHeightIndex.value = index
  selectedHeight.value = heightRanges.value[index]
  height.value = selectedHeight.value
  scrollToCenter(heightPicker.value, index)
  // 实时保存数据
  emit('data-change', {
    bodyType: bodyType.value,
    height: selectedHeight.value,
    weight: selectedWeight.value
  })
}

// 获取选项的实际宽度（从DOM元素实时获取）
const getItemWidth = (pickerElement) => {
  if (!pickerElement) return 80 // 默认值
  // 查找第一个实际的选项元素（跳过padding）
  const items = pickerElement.querySelectorAll('.horizontal-picker-item')
  if (items.length > 0) {
    const firstItem = items[0]
    const rect = firstItem.getBoundingClientRect()
    return rect.width || 80
  }
  return 80 // 默认值
}

// 根据DOM元素的实际位置计算最接近中心的索引（更可靠的方法）
const getClosestIndexByPosition = (pickerElement) => {
  if (!pickerElement) return -1
  
  const containerRect = pickerElement.getBoundingClientRect()
  const containerCenterViewport = containerRect.left + containerRect.width / 2
  
  const items = pickerElement.querySelectorAll('.horizontal-picker-item')
  let closestIndex = -1
  let minDistance = Infinity
  
  items.forEach((item, index) => {
    const itemRect = item.getBoundingClientRect()
    const itemCenterViewport = itemRect.left + itemRect.width / 2
    const distance = Math.abs(itemCenterViewport - containerCenterViewport)
    
    // 如果距离相同，优先选择索引更大的（确保最后两个选项能被选中）
    if (distance < minDistance || (distance === minDistance && index > closestIndex)) {
      minDistance = distance
      closestIndex = index
    }
  })
  
  // 特殊处理：如果滚动到底部，确保最后两个选项能被识别
  const maxScrollLeft = pickerElement.scrollWidth - pickerElement.clientWidth
  const currentScrollLeft = pickerElement.scrollLeft
  
  // 如果接近底部，且识别到的是倒数第三个或更早的，强制使用最后两个
  if (currentScrollLeft >= maxScrollLeft - 20 && items.length >= 2) {
    const lastIndex = items.length - 1
    const secondLastIndex = items.length - 2
    
    // 如果当前识别的是倒数第三个或更早，检查最后两个选项的位置
    if (closestIndex <= items.length - 3) {
      const lastItemRect = items[lastIndex]?.getBoundingClientRect()
      const secondLastItemRect = items[secondLastIndex]?.getBoundingClientRect()
      
      if (lastItemRect) {
        const lastItemCenter = lastItemRect.left + lastItemRect.width / 2
        const lastDistance = Math.abs(lastItemCenter - containerCenterViewport)
        if (lastDistance < minDistance * 2) { // 如果最后一项距离不是太远，选择它
          closestIndex = lastIndex
        } else if (secondLastItemRect) {
          const secondLastItemCenter = secondLastItemRect.left + secondLastItemRect.width / 2
          const secondLastDistance = Math.abs(secondLastItemCenter - containerCenterViewport)
          if (secondLastDistance < minDistance * 2) {
            closestIndex = secondLastIndex
          }
        }
      }
    }
  }
  
  return closestIndex
}

// 身高滚动监听 - 实时更新选中项
let heightScrollTimer = null
const handleHeightScroll = () => {
  if (!heightPicker.value) return

  // 清除之前的定时器
  if (heightScrollTimer) {
    clearTimeout(heightScrollTimer)
  }

  // 使用基于DOM位置的方法，直接找出最接近中心的选项（更可靠）
  let centerIndex = getClosestIndexByPosition(heightPicker.value)
  
  // 确保索引在有效范围内
  if (centerIndex < 0 || centerIndex >= heightRanges.value.length) {
    // 如果DOM方法失败，使用计算方式作为后备
    const scrollLeft = heightPicker.value.scrollLeft
    const itemWidth = getItemWidth(heightPicker.value)
    const containerWidth = heightPicker.value.clientWidth
    const paddingWidth = containerWidth / 2 - 20
    const containerCenter = containerWidth / 2
    const itemCenter = scrollLeft + containerCenter
    centerIndex = Math.round((itemCenter - paddingWidth - itemWidth / 2) / itemWidth)
    centerIndex = Math.max(0, Math.min(centerIndex, heightRanges.value.length - 1))
  }

  // 特殊处理：防止从最后两个选项回退
  const totalItems = heightRanges.value.length
  const lastIndex = totalItems - 1  // '> 190' 索引9
  const secondLastIndex = totalItems - 2  // '185-190' 索引8
  const maxScrollLeft = heightPicker.value.scrollWidth - heightPicker.value.clientWidth
  const currentScrollLeft = heightPicker.value.scrollLeft
  
  // 如果已经选中了最后两个选项之一，且滚动到底部附近，保持选中状态
  if (selectedHeightIndex.value >= secondLastIndex && currentScrollLeft >= maxScrollLeft - 30) {
    // 如果当前识别到的索引不是最后两个，且之前选中的是最后两个，保持之前的选择
    if (centerIndex < secondLastIndex) {
      // 检查最后两个选项是否在可见范围内
      const items = heightPicker.value.querySelectorAll('.horizontal-picker-item')
      if (items.length > secondLastIndex) {
        const secondLastItemRect = items[secondLastIndex]?.getBoundingClientRect()
        const lastItemRect = items[lastIndex]?.getBoundingClientRect()
        const containerRect = heightPicker.value.getBoundingClientRect()
        const containerCenter = containerRect.left + containerRect.width / 2
        
        // 如果最后两个选项中的任何一个在容器中心附近，使用它
        if (lastItemRect) {
          const lastItemCenter = lastItemRect.left + lastItemRect.width / 2
          if (Math.abs(lastItemCenter - containerCenter) < containerRect.width * 0.4) {
            centerIndex = lastIndex
          }
        }
        if (centerIndex < secondLastIndex && secondLastItemRect) {
          const secondLastItemCenter = secondLastItemRect.left + secondLastItemRect.width / 2
          if (Math.abs(secondLastItemCenter - containerCenter) < containerRect.width * 0.4) {
            centerIndex = secondLastIndex
          }
        }
      }
      // 如果最后两个选项都不在中心附近，但之前选中的是最后两个，保持当前选择
      if (centerIndex < secondLastIndex) {
        centerIndex = selectedHeightIndex.value
      }
    }
  }

  if (centerIndex !== selectedHeightIndex.value && centerIndex >= 0 && centerIndex < heightRanges.value.length) {
    selectedHeightIndex.value = centerIndex
    selectedHeight.value = heightRanges.value[centerIndex]
    height.value = selectedHeight.value
    // 实时保存数据
    emit('data-change', {
      bodyType: bodyType.value,
      height: selectedHeight.value,
      weight: selectedWeight.value
    })
  }

  // 滚动停止后自动吸附到中间
  heightScrollTimer = setTimeout(() => {
    // 使用基于DOM位置的方法，直接找出最接近中心的选项（更可靠）
    let finalIndex = getClosestIndexByPosition(heightPicker.value)
    
    // 确保索引在有效范围内
    if (finalIndex < 0 || finalIndex >= heightRanges.value.length) {
      // 如果DOM方法失败，使用计算方式作为后备
      const finalScrollLeft = heightPicker.value.scrollLeft
      const finalItemWidth = getItemWidth(heightPicker.value)
      const finalContainerWidth = heightPicker.value.clientWidth
      const finalPaddingWidth = finalContainerWidth / 2 - 20
      const finalContainerCenter = finalContainerWidth / 2
      const finalItemCenter = finalScrollLeft + finalContainerCenter
      finalIndex = Math.round((finalItemCenter - finalPaddingWidth - finalItemWidth / 2) / finalItemWidth)
      finalIndex = Math.max(0, Math.min(finalIndex, heightRanges.value.length - 1))
    }
    
    // 防止从最后两个选项回退
    const finalTotalItems = heightRanges.value.length
    const finalLastIndex = finalTotalItems - 1
    const finalSecondLastIndex = finalTotalItems - 2
    const finalMaxScrollLeft = heightPicker.value.scrollWidth - heightPicker.value.clientWidth
    const finalScrollLeft = heightPicker.value.scrollLeft
    
    // 如果当前选中的是最后两个选项之一，且滚动到底部附近，优先保持或选择最后两个
    if (selectedHeightIndex.value >= finalSecondLastIndex && finalScrollLeft >= finalMaxScrollLeft - 30) {
      if (finalIndex < finalSecondLastIndex) {
        // 检查最后两个选项的位置
        const items = heightPicker.value.querySelectorAll('.horizontal-picker-item')
        if (items.length > finalSecondLastIndex) {
          const containerRect = heightPicker.value.getBoundingClientRect()
          const containerCenter = containerRect.left + containerRect.width / 2
          
          const lastItemRect = items[finalLastIndex]?.getBoundingClientRect()
          const secondLastItemRect = items[finalSecondLastIndex]?.getBoundingClientRect()
          
          // 优先选择最后一个选项
          if (lastItemRect) {
            const lastItemCenter = lastItemRect.left + lastItemRect.width / 2
            if (Math.abs(lastItemCenter - containerCenter) < containerRect.width * 0.5) {
              finalIndex = finalLastIndex
            }
          }
          // 如果最后一个不在范围内，检查倒数第二个
          if (finalIndex < finalSecondLastIndex && secondLastItemRect) {
            const secondLastItemCenter = secondLastItemRect.left + secondLastItemRect.width / 2
            if (Math.abs(secondLastItemCenter - containerCenter) < containerRect.width * 0.5) {
              finalIndex = finalSecondLastIndex
            }
          }
        }
        // 如果都不在范围内，但之前选中的是最后两个，保持当前选择
        if (finalIndex < finalSecondLastIndex) {
          finalIndex = selectedHeightIndex.value
        }
      }
    }
    
    if (finalIndex >= 0 && finalIndex < heightRanges.value.length) {
      scrollToCenter(heightPicker.value, finalIndex)
    }
  }, 150)
}

// 体重选择
const selectWeight = (index) => {
  selectedWeightIndex.value = index
  selectedWeight.value = weightRanges.value[index]
  weight.value = selectedWeight.value
  scrollToCenter(weightPicker.value, index)
  // 实时保存数据
  emit('data-change', {
    bodyType: bodyType.value,
    height: selectedHeight.value,
    weight: selectedWeight.value
  })
}

// 体重滚动监听 - 实时更新选中项
let weightScrollTimer = null
const handleWeightScroll = () => {
  if (!weightPicker.value) return

  // 清除之前的定时器
  if (weightScrollTimer) {
    clearTimeout(weightScrollTimer)
  }

  // 实时获取元素尺寸
  const scrollLeft = weightPicker.value.scrollLeft
  const itemWidth = getItemWidth(weightPicker.value) // 从DOM实时获取
  const containerWidth = weightPicker.value.clientWidth
  const paddingWidth = containerWidth / 2 - 20
  const maxScrollLeft = weightPicker.value.scrollWidth - containerWidth
  // 计算当前容器中心对应的项索引
  const containerCenter = containerWidth / 2
  const itemCenter = scrollLeft + containerCenter
  let centerIndex = Math.round((itemCenter - paddingWidth - itemWidth / 2) / itemWidth)
  
  // 如果滚动到底部，确保能选中最后几个选项
  if (scrollLeft >= maxScrollLeft - 1) {
    const totalItems = weightRanges.value.length
    const lastItemCenter = paddingWidth + (totalItems - 1) * itemWidth + itemWidth / 2
    const lastItemScrollLeft = lastItemCenter - containerCenter
    if (scrollLeft >= lastItemScrollLeft - itemWidth / 2) {
      centerIndex = totalItems - 1
    } else if (scrollLeft >= lastItemScrollLeft - itemWidth * 1.5) {
      centerIndex = totalItems - 2
    }
  }
  
  // 确保索引在有效范围内
  centerIndex = Math.max(0, Math.min(centerIndex, weightRanges.value.length - 1))

  if (centerIndex !== selectedWeightIndex.value && centerIndex >= 0 && centerIndex < weightRanges.value.length) {
    selectedWeightIndex.value = centerIndex
    selectedWeight.value = weightRanges.value[centerIndex]
    weight.value = selectedWeight.value
    // 实时保存数据
    emit('data-change', {
      bodyType: bodyType.value,
      height: selectedHeight.value,
      weight: selectedWeight.value
    })
  }

  // 滚动停止后自动吸附到中间
  weightScrollTimer = setTimeout(() => {
    // 实时获取元素尺寸（可能在滚动过程中尺寸发生变化）
    const finalScrollLeft = weightPicker.value.scrollLeft
    const finalItemWidth = getItemWidth(weightPicker.value) // 实时获取
    const finalContainerWidth = weightPicker.value.clientWidth
    const finalPaddingWidth = finalContainerWidth / 2 - 20
    const finalContainerCenter = finalContainerWidth / 2
    const finalItemCenter = finalScrollLeft + finalContainerCenter
    let finalIndex = Math.round((finalItemCenter - finalPaddingWidth - finalItemWidth / 2) / finalItemWidth)
    
    // 如果滚动到底部，确保能选中最后几个选项
    const finalMaxScrollLeft = weightPicker.value.scrollWidth - finalContainerWidth
    if (finalScrollLeft >= finalMaxScrollLeft - 1) {
      const totalItems = weightRanges.value.length
      const lastItemCenter = finalPaddingWidth + (totalItems - 1) * finalItemWidth + finalItemWidth / 2
      const lastItemScrollLeft = lastItemCenter - finalContainerCenter
      if (finalScrollLeft >= lastItemScrollLeft - finalItemWidth / 2) {
        finalIndex = totalItems - 1
      } else if (finalScrollLeft >= lastItemScrollLeft - finalItemWidth * 1.5) {
        finalIndex = totalItems - 2
      }
    }
    
    // 确保索引在有效范围内
    finalIndex = Math.max(0, Math.min(finalIndex, weightRanges.value.length - 1))
    
    if (finalIndex >= 0 && finalIndex < weightRanges.value.length) {
      scrollToCenter(weightPicker.value, finalIndex)
    }
  }, 150)
}

// 滚动到居中位置（横向）
const scrollToCenter = (element, index) => {
  if (!element) return

  // 获取所有选项元素
  const items = element.querySelectorAll('.horizontal-picker-item')
  if (items.length === 0) return

  // 如果索引是最后几个选项，直接使用DOM元素位置来滚动
  const totalItems = items.length
  const lastIndex = totalItems - 1
  const secondLastIndex = totalItems - 2

  // 对于最后两个选项，使用更智能的滚动方式
  if (index >= secondLastIndex) {
    const targetItem = items[index]
    if (targetItem) {
      const maxScrollLeft = element.scrollWidth - element.clientWidth
      const currentScrollLeft = element.scrollLeft

      // 获取容器和元素的边界框
      const containerRect = element.getBoundingClientRect()
      const itemRect = targetItem.getBoundingClientRect()

      // 计算容器中心在视口中的位置
      const containerCenterX = containerRect.left + containerRect.width / 2

      // 计算元素中心在视口中的位置
      const itemCenterX = itemRect.left + itemRect.width / 2

      // 计算需要滚动的偏移量（元素中心相对于容器中心的偏移）
      const offsetX = itemCenterX - containerCenterX

      // 当前滚动位置加上偏移量
      let newScrollLeft = currentScrollLeft + offsetX

      // 对于最后两个选项，如果计算出的位置超过最大滚动位置
      // 直接滚动到最大位置，这样可以确保选项可见
      if (newScrollLeft > maxScrollLeft) {
        newScrollLeft = maxScrollLeft
      }

      // 确保滚动位置在有效范围内
      newScrollLeft = Math.max(0, Math.min(newScrollLeft, maxScrollLeft))

      // 使用requestAnimationFrame确保DOM更新后再滚动
      requestAnimationFrame(() => {
        element.scrollTo({
          left: newScrollLeft,
          behavior: 'smooth'
        })
      })
      return
    }
  }

  // 对于其他选项，使用计算方式
  const itemWidth = getItemWidth(element)
  const containerWidth = element.clientWidth
  const maxScrollLeft = element.scrollWidth - element.clientWidth

  // 计算padding宽度 (50% - 20px)
  const paddingWidth = containerWidth / 2 - 20
  // 计算选中项的中心位置：padding + index * itemWidth + itemWidth/2
  // 要让这个中心对齐到容器中心，所以滚动位置 = 选中项中心 - 容器中心
  const itemCenter = paddingWidth + index * itemWidth + itemWidth / 2
  const containerCenter = containerWidth / 2
  let scrollLeft = itemCenter - containerCenter

  // 确保滚动位置在有效范围内
  if (scrollLeft < 0) {
    scrollLeft = 0
  } else if (scrollLeft > maxScrollLeft) {
    // 如果计算出的滚动位置超过最大值，说明这个索引无法完全居中
    // 此时滚动到最大位置，这样可以确保最后几个选项也能被选中
    scrollLeft = maxScrollLeft
  }

  element.scrollTo({
    left: scrollLeft,
    behavior: 'smooth'
  })
}



// 返回上一页（这里可以发射事件给父组件处理）
const goBack = () => {
  // 在返回前确保保存当前数据
  emit('data-change', {
    bodyType: bodyType.value,
    height: selectedHeight.value,
    weight: selectedWeight.value
  })
  // 延迟一下再返回，确保数据保存完成
  nextTick(() => {
    emit('back')
  })
}

// 进入下一页
const goNext = () => {
  if (!isFormValid.value) return

  // 在跳转前确保保存当前数据
  const stepData = {
    bodyType: bodyType.value,
    height: selectedHeight.value,
    weight: selectedWeight.value
  }

  // 先保存数据
  emit('data-change', stepData)

  // 立即跳转，不等待 ok() 完成
  emit('next', stepData)

  // 在后台异步执行 ok 函数（不阻塞跳转）
  ok().catch(error => {
    console.error('后台 API 调用失败:', error)
  })
}

// 为了兼容，保持handleSubmit方法名
const handleSubmit = goNext

// 暴露方法给父组件调用
defineExpose({
  handleSubmit
})

// 清理定时器
onBeforeUnmount(() => {
  if (heightScrollTimer) {
    clearTimeout(heightScrollTimer)
    heightScrollTimer = null
  }
  if (weightScrollTimer) {
    clearTimeout(weightScrollTimer)
    weightScrollTimer = null
  }
})
</script>

<style scoped>
/* 主容器 - 全屏竖屏布局 */
.personal-info-container {
  width: 100vw;
  height: 100dvh;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  overflow: hidden;
}

.personal-info-container::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg,
    rgba(255, 255, 255, 0.1) 0%,
    rgba(255, 255, 255, 0.05) 50%,
    rgba(255, 255, 255, 0.02) 100%);
  pointer-events: none;
  border-radius: 0;
}

/* 标题样式 */
.title {
  color: rgba(255, 255, 255, 0.95);
  font-size: 24px;
  font-weight: 1000;
  margin-top: 10%;
  margin-bottom: 30px;
  letter-spacing: 0.5px;
  line-height: 1.2;
  text-align: center;
  position: relative;
  z-index: 1;
  flex-shrink: 0;
}

/* 主要内容区域 */
.main-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  width: calc(100% - 32px);
  max-width: 420px;
  margin: 0 auto;
  position: relative;
  z-index: 1;
  flex: 1;
  max-height: calc(100dvh - 200px);
  overflow-y: auto;
}

/* 数据卡片 */
.data-card {
  width: 100%;
  background: rgba(35, 35, 35, 0.9);
  border-radius: 24px;
  padding: 28px 20px;
  backdrop-filter: blur(20px);
  display: flex;
  flex-direction: column;
  position: relative;
  isolation: isolate;
}

.data-card::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: 24px;
  padding: 1px;
  background: linear-gradient(135deg,
    rgba(255, 255, 255, 1),
    rgba(204, 204, 204, 0.8),
    rgba(204, 204, 204, 0.4),
    rgba(204, 204, 204, 0.4),
    rgba(204, 204, 204, 0.8),
    rgba(255, 255, 255, 0.9));
  -webkit-mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  mask-composite: exclude;
  pointer-events: none;
  z-index: -1;
}

.data-card > * {
  position: relative;
  z-index: 1;
}

/* 表单内容区域 */
.form-content {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 30px;
  align-items: stretch;
}
/*@media (orientation: portrait) {
    .title{
        margin-bottom: 2%;
        color: rgba(255, 255, 255, 0.95);
        font-size: 26px;
        font-weight: 600;
        letter-spacing: 1px;
        line-height: 1.3;
        text-align: center;
        position: relative;
        z-index: 1;
        text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
    }
    .main-content {
        display: flex;
        flex-direction: column;
        align-items: stretch;
        justify-content: center;
        width: 100%;
        position: relative;
        flex: 1;
        z-index: 1;
        max-width: 600px;
        margin: 0 auto;
        margin-bottom: 24%;
    }
    .personal-info-container{
        transform: scale(2);
    }
}*/



.sub-title {
    color: rgba(255, 255, 255, 0.9);
    font-weight: 600;
    font-size: 18px;
    text-transform: lowercase;
    letter-spacing: 0.5px;
    margin-bottom: 20px;
}

.bodies {
    display: flex;
    justify-content: space-between;
    gap: 10px;
    width: 100%;
    flex-wrap: wrap;
    padding: 0 5px;
}

.body-card {
    flex: 1;
    min-width: 0;
    height: 230px;
    border-radius: 16px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    cursor: pointer;
    /*background: rgba(255, 255, 255, 0.08);*/
    /*border: 2px solid rgba(255, 255, 255, 0.15);*/
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    padding: 15px 8px;
    position: relative;
    overflow: hidden;
}

.body-card::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg,
        rgba(255, 255, 255, 0.1) 0%,
        rgba(255, 255, 255, 0.05) 100%);
    opacity: 0;
    transition: opacity 0.3s ease;
    border-radius: 14px;
}

.body-card:hover::before {
    opacity: 1;
}

.body-card.active {
    background: rgba(255,255,255,0.2);
    box-shadow: inset 0px 0px 10px 2px rgba(0,0,0,0.8);
    transform: translateY(-2px);
}

.body-card:hover:not(.active) {
    background: rgba(255, 255, 255, 0.12);
    border-color: rgba(255, 255, 255, 0.25);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.figure {
    width: 100px;
    height: 100px;
    object-fit: contain;
    border-radius: 6px;
    opacity: 0.9;
    transition: all 0.3s ease;
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
}

.body-card.active .figure {
    /* 让图标填充绿色 */
    fill: rgba(166, 245, 93, 1);
    stroke: rgba(166, 245, 93, 1);
}

.body-name {
    color: rgba(255, 255, 255, 0.85);
    font-size: 23px;
    font-weight: 800;
    text-align: center;
    transition: all 0.3s ease;
}

.body-nameCheck {
    color: #4CAF50;
    font-size: 23px;
    font-weight: 800;
    text-align: center;
    transition: all 0.3s ease;
}

.body-card.active .body-name {
    color: rgba(255, 255, 255, 1);
    font-weight: 600;
}

/* 表单组样式 */
.form-group {
  display: flex;
  flex-direction: column;
  gap: 18px;
  width: 100%;
}

/* 标签样式 */
.form-label {
  color: rgba(255, 255, 255, 0.95);
  font-size: 16px;
  font-weight: 500;
  letter-spacing: 0.5px;
  text-align: center;
  margin-bottom: 12px;
}

/* 横向选择器容器样式 */
.horizontal-picker-container {
  position: relative;
  width: 100%;
  height: 60px;
  margin: 0 auto;
  overflow: hidden;
}

.horizontal-picker-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 2;
}

.horizontal-picker-overlay::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  bottom: 0;
  width: 80px;
}

.horizontal-picker-overlay::after {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 80px;
}

.horizontal-picker-center {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 50%;
  width: 80px;
  transform: translateX(-50%);
    background: rgba(255,255,255,0.2);
    box-shadow: inset 0px 0px 4px 1px rgba(0,0,0,0.8);
    border-radius: 4px 4px 4px 4px;
    border: 1px solid rgba(0,0,0,0.4);
  pointer-events: none;
  border-radius: 8px;
  z-index: 1;
}

.horizontal-picker-scroll {
  width: 100%;
  height: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  scroll-behavior: smooth;
  scroll-snap-type: x mandatory;
  -ms-overflow-style: none;
  scrollbar-width: none;
  display: flex;
  align-items: center;
}

.horizontal-picker-scroll::-webkit-scrollbar {
  display: none;
}

.horizontal-picker-padding {
  width: calc(50%);
  flex-shrink: 0;
}
.horizontal-picker-padding-right {
  width: calc(70%);
  flex-shrink: 0;
}

.horizontal-picker-item {
  width: 80px;
  height: 100%;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.5);
  font-size: 14px;
  font-weight: 400;
  cursor: pointer;
  transition: all 0.3s ease;
  scroll-snap-align: center;
  user-select: none;
  border-radius: 8px;
  white-space: nowrap;
}

.horizontal-picker-item.selected {
  background: transparent;
  color: rgba(255, 255, 255, 1);
  font-size: 15px;
  font-weight: 1000;
  /*text-shadow: 0 0 8px rgba(166, 245, 93, 0.5);*/
  transform: scale(1.1);
  transition: all 0.2s ease;
}

.horizontal-picker-item:hover:not(.selected) {
  color: rgba(255, 255, 255, 0.7);
  background: rgba(50, 50, 50, 0.5);
}

/* 底部操作按钮 */
.bottom-actions {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 5%;
  padding: 16px 20px calc(16px + env(safe-area-inset-bottom, 0px));
  display: flex;
  gap: 12px;
  justify-content: space-between;
  /*background: linear-gradient(180deg, rgba(10,10,10,0) 0%, rgba(10,10,10,0.8) 40%, rgba(10,10,10,0.95) 100%);*/
  /*backdrop-filter: blur(16px);*/
  z-index: 100;
  pointer-events: none;
}

.bottom-actions > * {
  pointer-events: auto;
}

.btn-secondary, .btn-primary {
  flex: 1;
  height: 48px;
  border-radius: 24px;
  border: none;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
}

.btn-secondary { 
  background: rgba(255, 255, 255, 0.12); 
  color: rgba(255, 255, 255, 0.95); 
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.btn-secondary:hover {
  background: rgba(255, 255, 255, 0.18);
}

.btn-secondary:active {
  transform: scale(0.98);
}

.btn-primary { 
  background: #A6F55D; 
  color: #111; 
  font-weight: 600;
  box-shadow: 0 4px 16px rgba(166, 245, 93, 0.3);
}

.btn-primary:hover:not(.disabled) {
  background: #95E04C;
}

.btn-primary:active:not(.disabled) {
  transform: scale(0.98);
}

.btn-primary.disabled { 
  opacity: 0.4; 
  cursor: not-allowed;
  box-shadow: none;
}

.btn-icon {
  font-size: 18px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-icon img {
  display: block;
  width: 20px;
  height: 20px;
  vertical-align: middle;
}

.btn-text {
  font-size: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
  line-height: 1;
}

/* 按钮内容容器 */
.button-content {
  display: flex;
  align-items: center;
  gap: 8px;
}

/* 加载状态样式 */
.loading-content {
  display: flex;
  align-items: center;
  gap: 12px;
  justify-content: center;
}

.loading-spinner {
  width: 20px;
  height: 20px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top: 2px solid rgba(255, 255, 255, 1);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.loading-text {
  color: rgba(255, 255, 255, 0.95);
  font-size: 16px;
  font-weight: 500;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

</style>

